// GoHighLevel CRM API Service
// API key stored in env var VITE_GHL_API_KEY
// Base URL: https://rest.gohighlevel.com/v1/

const GHL_BASE_URL = 'https://rest.gohighlevel.com/v1'

function getApiKey() {
  return import.meta.env.VITE_GHL_API_KEY || localStorage.getItem('ghl_api_key') || ''
}

function ghlHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getApiKey()}`,
  }
}

async function ghlFetch(path, options = {}) {
  const apiKey = getApiKey()
  if (!apiKey) {
    console.warn('[GHL] No API key configured. Set VITE_GHL_API_KEY or use Settings > GHL Config.')
    return { error: 'No GHL API key configured', data: null }
  }
  try {
    const res = await fetch(`${GHL_BASE_URL}${path}`, {
      ...options,
      headers: { ...ghlHeaders(), ...(options.headers || {}) },
    })
    if (!res.ok) {
      const err = await res.text()
      return { error: `GHL API error ${res.status}: ${err}`, data: null }
    }
    const data = await res.json()
    return { data, error: null }
  } catch (e) {
    return { error: e.message, data: null }
  }
}

// ── Contacts ──────────────────────────────────────────────────────────────────

/**
 * Create or update a contact in GHL.
 * Called on lead capture form submit.
 */
export async function syncContact(contact) {
  const payload = {
    firstName: contact.firstName || contact.name?.split(' ')[0] || '',
    lastName:  contact.lastName  || contact.name?.split(' ').slice(1).join(' ') || '',
    email:     contact.email     || '',
    phone:     contact.phone     || '',
    customField: {
      role:     contact.role     || '',
      income:   contact.income   || '',
      capital:  contact.capital  || '',
      timeline: contact.timeline || '',
      intent:   contact.intent   || '',
    },
  }
  return ghlFetch('/contacts/', { method: 'POST', body: JSON.stringify(payload) })
}

export async function getContact(contactId) {
  return ghlFetch(`/contacts/${contactId}`)
}

export async function searchContacts(query) {
  return ghlFetch(`/contacts/?query=${encodeURIComponent(query)}`)
}

export async function getAllContacts(limit = 100) {
  return ghlFetch(`/contacts/?limit=${limit}`)
}

// ── Pipelines ─────────────────────────────────────────────────────────────────

export async function getPipelines() {
  return ghlFetch('/pipelines/')
}

export async function getPipelineStages(pipelineId) {
  return ghlFetch(`/pipelines/${pipelineId}`)
}

// ── Opportunities (Deals) ─────────────────────────────────────────────────────

export async function getOpportunities(pipelineId) {
  return ghlFetch(`/pipelines/${pipelineId}/opportunities`)
}

export async function updateOpportunityStage(pipelineId, opportunityId, stageId, customFields = {}) {
  return ghlFetch(`/pipelines/${pipelineId}/opportunities/${opportunityId}`, {
    method: 'PATCH',
    body: JSON.stringify({ stageId, customFields }),
  })
}

export async function createOpportunity(pipelineId, data) {
  return ghlFetch(`/pipelines/${pipelineId}/opportunities`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Persist module stage to GHL custom field cf_module_stage
 */
export async function persistModuleStage(pipelineId, opportunityId, moduleStage) {
  return updateOpportunityStage(pipelineId, opportunityId, null, {
    cf_module_stage: String(moduleStage),
  })
}

// ── Conversations ─────────────────────────────────────────────────────────────

export async function getConversations(contactId) {
  return ghlFetch(`/conversations/?contactId=${contactId}`)
}

// ── Automations ──────────────────────────────────────────────────────────────

export async function getAutomations() {
  return ghlFetch('/workflows/')
}

// ── Stage mapping for progress bar (Task 11) ────────────────────────────────

export const GHL_STAGE_PROGRESS = {
  Lead:             10,
  Qualified:        30,
  'Call Scheduled': 50,
  Negotiation:      65,
  'Under Contract': 80,
  Closed:           100,
}

export function stageToProgress(stageName) {
  return GHL_STAGE_PROGRESS[stageName] ?? 10
}
